// Library imports
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import store, { RootState } from "store";
import styled from "styled-components";
import axios from "axios";

// Local imports
import {
  AUDIENCE_LISTS,
  AUDIENCE_LISTS_NON_PROFILED_CONTACTS,
  AUDIENCE_LISTS_PROFILED_CONTACTS,
} from "constants/types";
import FloatingSidebarTwo from "modules/Common/Components/FloatingSidebarTwo";
import { closeDetailProfile, GLOBAL_ACTION } from "modules/GlobalActions";
import { getQueryListId } from "utils";
import CreateContactSidebar from "../People/CreateContactSidebar";
import {
  getAudienceListDetails,
  reloadListDetails,
  resetAudienceListDetails,
  resetAudienceListDetailsPeopleSearch,
  resetAudienceListDetailsProfiled,
} from "./actions";
import ListDetailsTab from "./ListDetailsTab";
import ProfiledList from "./ProfiledList";
import { getDesignations, getOrganizations } from "../actions";

interface IAudienceListView {
  showImportListAsider: boolean;
  setShowImportListAsider: (arg0: boolean) => void;
  setTabState: (arg0: string) => void;
  tabState: string;
  page: string;
}

const AudienceListView: React.FC<IAudienceListView> = ({
  showImportListAsider,
  setShowImportListAsider,
  tabState,
  setTabState,
  page,
}) => {
  // Local states
  let source = axios.CancelToken.source();
  const [updateTotalCount, setUpdateTotalCount] = useState<boolean>(false);
  const [skipInitialMount, setSkipInitialMount] = useState(true);
  const [selectedOrganization, setSelectedOrganization] = useState<string[]>([]); // organization filter state
  const [selectedDesignation, setSelectedDesignation] = useState<string[]>([]); // designation filter state
  const [selectedPersonalityType, setSelectedPersonalityType] = useState<string[]>([]); // personality filter state
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]); // status filter state
  const [clearFiltersCheck, setClearFiltersCheck] = useState(false); // alter this state to trigger get contacts call without any filters
  const [exportContactsFilterParams, setExportContactsFilterParams] = useState({});

  // Redux states
  const listId: string = useSelector((state: RootState) => state.audienceListDetails.listId);
  const newlyCreatedListId: string = useSelector(
    (state: RootState) => state.audienceListDetails.newlyCreatedListId
  );
  const openEditContactSidebar = useSelector((state: RootState) => state.peopleAudience.openEditContactSidebar);
  const audienceFiltersApplied: boolean = useSelector(
    (state: RootState) => state.uiReducer.audienceFiltersApplied
  );
  const searchTermListDetails = useSelector((state: RootState) => state.audienceListDetails.searchTermListDetails);

  // Component Logic
  useEffect(() => {
    if (
      selectedOrganization.length === 0 &&
      selectedDesignation.length === 0 &&
      selectedPersonalityType.length === 0 &&
      selectedStatus.length === 0 &&
      audienceFiltersApplied
    ) {
      clearFiltersHandler();
    }
  }, [selectedOrganization, selectedDesignation, selectedPersonalityType, selectedStatus]);

  const resetFilters = () => {
    setSelectedOrganization([]);
    setSelectedDesignation([]);
    setSelectedPersonalityType([]);
    setSelectedStatus([]);
    store.dispatch({
      type: AUDIENCE_LISTS_PROFILED_CONTACTS.SET_FILTERS,
      filters: {
        designations: [],
        organizations: [],
        personality: [],
        status: "",
      },
    });
    resetAudienceListDetailsProfiled();
  };

  const exportPeopleFiltersUpdate = () => {
    let exportContactsFilterParamsObj: any = {};
    if (selectedOrganization.length > 0) {
      exportContactsFilterParamsObj["filter_by_organization"] = selectedOrganization;
    } else {
      delete exportContactsFilterParamsObj["filter_by_organization"];
    }
    if (selectedDesignation.length > 0) {
      exportContactsFilterParamsObj["filter_by_designation"] = selectedDesignation;
    } else {
      delete exportContactsFilterParamsObj["filter_by_designation"];
    }
    if (selectedPersonalityType.length > 0) {
      exportContactsFilterParamsObj["personality_type"] = selectedPersonalityType;
    } else {
      delete exportContactsFilterParamsObj["personality_type"];
    }
    if (selectedStatus.length > 0) {
      exportContactsFilterParamsObj["status"] = selectedStatus;
    } else {
      delete exportContactsFilterParamsObj["status"];
    }
    setExportContactsFilterParams(exportContactsFilterParamsObj);
    store.dispatch({
      type: AUDIENCE_LISTS.FILTERS,
      filters: exportContactsFilterParamsObj,
    });
  };

  const clearFiltersHandler = () => {
    resetFilters();
    exportPeopleFiltersUpdate(); // reset the filters in the store for export
    setClearFiltersCheck(!clearFiltersCheck);
    resetAudienceListDetailsProfiled(); // reset list contacts on clear filters
    if (audienceFiltersApplied) {
      store.dispatch({
        type: GLOBAL_ACTION.AUDIENCE_FILTERS_APPLIED,
        payload: false,
      });
    }
  };

  useEffect(() => {
    if (skipInitialMount) {
      setSkipInitialMount(false);
    } else {
      resetFilters();
    }
  }, [tabState]);

  useEffect(() => {
    if (listId !== newlyCreatedListId.toString()) {
      getAudienceListDetails(source.token); // get newly created list details
      clearFiltersHandler(); // reset filters on list change
    }
  }, [newlyCreatedListId]);

  useEffect(() => {
    if (listId !== getQueryListId().toString()) {
      resetAudienceListDetails();
      getAudienceListDetails(source.token);
      getOrganizations(getQueryListId().toString());
      getDesignations(getQueryListId().toString());
    }
    return () => {
      // reload list after import
      let reloadList = store.getState().audienceListDetails.reloadList;
      if (reloadList) {
        reloadListDetails(false, tabState);
      }
      // cancelling the request when unmounted
      if (source) {
        source.cancel("List Details Component fot Unmount.");
      }
      store.dispatch({
        type: GLOBAL_ACTION.AUDIENCE_FILTERS_APPLIED,
        payload: false,
      });
      closeDetailProfile(); // close pserson detail asider on unmount
      resetAudienceListDetailsPeopleSearch();
    };
  }, []);

  return (
    <Wrapper className="audience-content-inner-01">
      <div className="audience-content-inner">
        <ListDetailsTab
          tabState={"profiled"}
          setTabState={setTabState}
          showImportListAsider={showImportListAsider}
          setShowImportListAsider={setShowImportListAsider}
          updateTotalCount={updateTotalCount}
          setUpdateTotalCount={setUpdateTotalCount}
          selectedDesignation={selectedDesignation}
          setSelectedDesignation={setSelectedDesignation}
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
          selectedPersonalityType={selectedPersonalityType}
          setSelectedPersonalityType={setSelectedPersonalityType}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          resetFilters={resetFilters}
          clearFiltersCheck={clearFiltersCheck}
          setClearFiltersCheck={setClearFiltersCheck}
          clearFiltersHandler={clearFiltersHandler}
          exportContactsFilterParams={exportContactsFilterParams}
          exportPeopleFiltersUpdate={exportPeopleFiltersUpdate}
          page={page}
        />
        <ProfiledList
          isProfiled={true}
          setUpdateTotalCount={setUpdateTotalCount}
          tabState={"profiled"}
          clearFiltersCheck={clearFiltersCheck}
          exportPeopleFiltersUpdate={exportPeopleFiltersUpdate}
        />
      </div>
      <FloatingSidebarTwo isToggle={openEditContactSidebar}>
        <CreateContactSidebar detailProfileAsider={true} editSidebar={true} />
      </FloatingSidebarTwo>
    </Wrapper>
  );
};

export default AudienceListView;

const Wrapper = styled.div`
  .audience-content-inner {
    /* position: relative;
    z-index: 5; */
    .floating-footer {
      z-index: 5;
    }
    > div {
      z-index: 5;
    }
    .footer-sider {
      @media (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 330px;
      }
    }
  }
`;
